import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FullPageLoader, LoadingSpinner } from '../components/LoadingSpinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWxtb25kbWlsa3MiLCJhIjoiY21nZjczZDFrMDU3ODJpb3J1bXpmdTJ6MiJ9.WCVmPa68SdgT7PH4mGlB-w';
mapboxgl.accessToken = MAPBOX_TOKEN;

export default function MapView() {
  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const { tripId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  // OPTIMIZATION: Fetch trip and participants in parallel
  useEffect(() => {
    fetchData();
  }, [tripId]);

  useEffect(() => {
    if (participants.length > 0 && !map.current) {
      initializeMap();
    }
  }, [participants]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Parallel API calls for faster loading
      const [tripResponse, participantsResponse] = await Promise.all([
        axios.get(`${API_URL}/trips/${tripId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/trips/${tripId}/participants`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTrip(tripResponse.data);
      setParticipants(participantsResponse.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddresses = async () => {
    setGeocoding(true);
    try {
      const addresses = participants.map(p => p.address);
      await axios.post(
        `${API_URL}/geocode`,
        addresses,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Addresses geocoded successfully!');
      
      // Refetch only participants after geocoding
      const participantsResponse = await axios.get(`${API_URL}/trips/${tripId}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(participantsResponse.data);
    } catch (error) {
      toast.error('Failed to geocode addresses');
    } finally {
      setGeocoding(false);
    }
  };

  const initializeMap = () => {
    if (map.current) return;

    const participantsWithCoords = participants.filter(p => p.lat && p.lng);
    if (participantsWithCoords.length === 0) return;

    // Calculate bounds
    const bounds = new mapboxgl.LngLatBounds();
    participantsWithCoords.forEach(p => {
      bounds.extend([p.lng, p.lat]);
    });

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      bounds: bounds,
      fitBoundsOptions: { padding: 50 }
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    addMarkers();
  };

  const addMarkers = () => {
    if (!map.current) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    const participantsWithCoords = participants.filter(p => p.lat && p.lng);

    participantsWithCoords.forEach(p => {
      const name = p.role === 'driver'
        ? `${p.driver_first_name || ''} ${p.driver_last_name || ''}`.trim()
        : `${p.passenger_first_name || ''} ${p.passenger_last_name || ''}`.trim();

      const el = document.createElement('div');
      el.className = 'custom-marker';
      // Feature Update: Increased marker size to 22px (20% bigger than 18px)
      el.style.width = '22px';
      el.style.height = '22px';
      el.style.borderRadius = '50%';
      el.style.border = '2px solid white';
      el.style.backgroundColor = p.role === 'driver' ? '#3B82F6' : '#10B981';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.color = 'white';
      el.style.fontWeight = 'bold';
      el.style.fontSize = '10px';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

      if (p.role === 'driver') {
        el.textContent = p.capacity || 0;
      }

      const popup = new mapboxgl.Popup({ 
        offset: 15,
        closeButton: false,
        closeOnClick: false
      }).setHTML(`
        <div style="font-family: Inter, sans-serif; padding: 4px;">
          <p style="font-weight: bold; margin-bottom: 4px; font-size: 13px;">${name || 'N/A'}</p>
          <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Role:</strong> ${p.role}</p>
          <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Phone:</strong> ${p.phone}</p>
          <p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Address:</strong> ${p.address}</p>
          ${p.role === 'driver' ? `<p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Capacity:</strong> ${p.capacity || 0}</p>` : ''}
          ${p.car_license_type ? `<p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Car/License:</strong> ${p.car_license_type}</p>` : ''}
          ${p.notes ? `<p style="font-size: 11px; color: #64748b; margin: 2px 0;"><strong>Notes:</strong> ${p.notes}</p>` : ''}
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([p.lng, p.lat])
        .setPopup(popup)
        .addTo(map.current);

      // Show popup on hover
      el.addEventListener('mouseenter', () => {
        popup.addTo(map.current);
      });

      el.addEventListener('mouseleave', () => {
        popup.remove();
      });

      markers.current.push(marker);
    });
  };

  const toggleMapStyle = () => {
    const newStyle = mapStyle === 'mapbox://styles/mapbox/streets-v12' 
      ? 'mapbox://styles/mapbox/dark-v11' 
      : 'mapbox://styles/mapbox/streets-v12';
    
    setMapStyle(newStyle);
    
    if (map.current) {
      map.current.setStyle(newStyle);
      map.current.once('style.load', () => {
        addMarkers();
      });
    }
  };

  const hasGeocodedParticipants = useMemo(() => 
    participants.some(p => p.lat && p.lng),
    [participants]
  );

  if (loading) {
    return <FullPageLoader text="Loading map..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {trip?.name || 'Trip'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">Step 2: Map & Review</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/trip/${tripId}/upload`)}
                data-testid="back-to-upload"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => navigate(`/trip/${tripId}/allocate`)}
                disabled={!hasGeocodedParticipants}
                data-testid="next-to-allocate"
              >
                Next: Allocate
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hasGeocodedParticipants ? (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Geocode Addresses</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Before viewing the map, we need to convert addresses to geographic coordinates.
              </p>
              <Button
                onClick={geocodeAddresses}
                disabled={geocoding || loading}
                data-testid="geocode-button"
              >
                {geocoding ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <MapPin className="w-4 h-4 mr-2" />
                    Geocode All Addresses
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Controls */}
            <div className="mb-4 flex justify-between items-center">
              {/* Legend */}
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">3</div>
                  <span className="text-sm text-slate-700">Drivers (number = capacity)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-500 border-2 border-white"></div>
                  <span className="text-sm text-slate-700">Passengers</span>
                </div>
              </div>

              {/* Map Style Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleMapStyle}
                data-testid="map-style-toggle"
              >
                {mapStyle === 'mapbox://styles/mapbox/streets-v12' ? '🌙 Dark Map' : '☀️ Light Map'}
              </Button>
            </div>

            {/* Map */}
            <div
              ref={mapContainer}
              className="w-full h-[600px] rounded-lg shadow-lg mb-8"
              data-testid="map-container"
            />

            {/* Legend at bottom */}
            <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">Map Legend:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• <strong>Hover</strong> over any marker to see participant details</li>
                <li>• <strong>Blue dots</strong> are drivers - the number shows available seats</li>
                <li>• <strong>Green dots</strong> are passengers</li>
                <li>• Use the toggle above to switch between light and dark map styles</li>
              </ul>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}
